import api from './api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  firm_name: string;
  firm_address: string;
  firm_city: string;
  firm_state: string;
  firm_zip: string;
  firm_phone: string;
  firm_website: string;
}

export interface SocialRegisterData {
  token: string;
  firm_name: string;
  firm_address?: string;
  firm_city?: string;
  firm_state?: string;
  firm_zip?: string;
  firm_phone?: string;
  accept_terms: boolean;
}

interface Role {
  id: number;
  name: string;
  guard_name: string;
  created_at: string;
  updated_at: string;
  permissions?: {
    id: number;
    name: string;
    guard_name: string;
    created_at: string;
    updated_at: string;
  }[];
  pivot: {
    model_type: string;
    model_id: number;
    role_id: number;
  };
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    name: string;
    email: string;
    email_verified_at: string | null;
    firm_id: number;
    is_approved: boolean;
    provider: string | null;
    provider_id: string | null;
    created_at: string;
    updated_at: string;
    roles: Role[];
    permissions: string[];
    avatar?: string;
    firm?: {
      id: number;
      name: string;
      address: string;
      city: string;
      state: string;
      zip: string;
      phone: string;
      website: string;
      status: string;
      rejection_reason: string | null;
      created_at: string;
      updated_at: string;
    };
  };
  impersonating?: boolean;
  impersonator?: ImpersonatorInfo;
}

export interface ImpersonatorInfo {
  id: number;
  name: string;
  email: string;
}

export interface UserResponse {
  user: AuthResponse['user'];
  impersonating?: boolean;
  impersonator?: ImpersonatorInfo;
}

const MASQUERADE_ORIGINAL_TOKEN_KEY = 'masquerade_original_token';
const MASQUERADE_ORIGINAL_USER_KEY = 'masquerade_original_user';
const MASQUERADE_IMPERSONATOR_KEY = 'masquerade_impersonator';

export interface SocialUserData {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  provider: string;
  registration_completed: boolean;
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/login', credentials);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Fetch fresh user data to ensure we have the firm relationship
      try {
        const userResponse = await api.get<UserResponse>('/user');
        this.persistUserResponse(userResponse.data);
      } catch (error) {
        console.error('Failed to fetch fresh user data after login:', error);
      }

      // Dispatch custom event to notify components of auth change
      window.dispatchEvent(new Event('auth-change'));
    }
    return response.data;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/register', data);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Fetch fresh user data to ensure we have the firm relationship
      try {
        const userResponse = await api.get<UserResponse>('/user');
        this.persistUserResponse(userResponse.data);
      } catch (error) {
        console.error(
          'Failed to fetch fresh user data after registration:',
          error
        );
      }

      // Dispatch custom event to notify components of auth change
      window.dispatchEvent(new Event('auth-change'));
    }
    return response.data;
  }

  async completeSocialRegistration(data: SocialRegisterData): Promise<any> {
    const response = await api.post('/social-register', data);
    return response.data;
  }

  async getSocialUserFromToken(token: string): Promise<SocialUserData> {
    const response = await api.post('/social-user-from-token', { token });
    return response.data.user;
  }

  async socialLogin(provider: string): Promise<string> {
    const response = await api.get<{ url: string }>(`/login/${provider}`);
    return response.data.url;
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this.clearMasqueradeState();

    // Dispatch custom event to notify components of auth change
    window.dispatchEvent(new Event('auth-change'));
  }

  isMasquerading(): boolean {
    return !!localStorage.getItem(MASQUERADE_ORIGINAL_TOKEN_KEY);
  }

  getImpersonator(): ImpersonatorInfo | null {
    const raw = localStorage.getItem(MASQUERADE_IMPERSONATOR_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  async startMasquerade(userId: number): Promise<void> {
    const originalToken = localStorage.getItem('token');
    const originalUser = localStorage.getItem('user');

    if (!originalToken || !originalUser) {
      throw new Error('You must be logged in to masquerade as another user.');
    }

    const response = await api.post<{
      success: boolean;
      data: AuthResponse;
      message?: string;
    }>(`/admin/users/${userId}/impersonate`);

    if (!response.data.success || !response.data.data?.access_token) {
      throw new Error(response.data.message || 'Failed to start masquerade session.');
    }

    const { access_token, user, impersonator } = response.data.data;

    localStorage.setItem(MASQUERADE_ORIGINAL_TOKEN_KEY, originalToken);
    localStorage.setItem(MASQUERADE_ORIGINAL_USER_KEY, originalUser);
    if (impersonator) {
      localStorage.setItem(MASQUERADE_IMPERSONATOR_KEY, JSON.stringify(impersonator));
    }

    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(user));
    window.dispatchEvent(new Event('auth-change'));
  }

  async stopMasquerade(): Promise<void> {
    const originalToken = localStorage.getItem(MASQUERADE_ORIGINAL_TOKEN_KEY);
    const originalUser = localStorage.getItem(MASQUERADE_ORIGINAL_USER_KEY);

    if (!originalToken || !originalUser) {
      throw new Error('No active masquerade session found.');
    }

    try {
      await api.post('/admin/impersonate/stop');
    } catch (error) {
      console.error('Failed to revoke masquerade token:', error);
    }

    localStorage.setItem('token', originalToken);
    localStorage.setItem('user', originalUser);
    this.clearMasqueradeState();
    window.dispatchEvent(new Event('auth-change'));
  }

  private clearMasqueradeState(): void {
    localStorage.removeItem(MASQUERADE_ORIGINAL_TOKEN_KEY);
    localStorage.removeItem(MASQUERADE_ORIGINAL_USER_KEY);
    localStorage.removeItem(MASQUERADE_IMPERSONATOR_KEY);
  }

  private persistUserResponse(data: UserResponse): void {
    localStorage.setItem('user', JSON.stringify(data.user));

    if (data.impersonating && data.impersonator) {
      localStorage.setItem(MASQUERADE_IMPERSONATOR_KEY, JSON.stringify(data.impersonator));
    } else if (!this.isMasquerading()) {
      localStorage.removeItem(MASQUERADE_IMPERSONATOR_KEY);
    }
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    return !!token;
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.roles?.some((r: Role) => r.name === role) || false;
  }

  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    // console.log(user)
    if (!user?.roles) return false;

    // Check if any of the user's roles have the specified permission
    return user.roles.some((role: Role) =>
      role.permissions?.some(p => p.name === permission)
    );
  }

  async refreshUserData(): Promise<any> {
    try {
      const response = await api.get<UserResponse>('/user');
      this.persistUserResponse(response.data);
      return response.data.user;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post('/forgot-password', { email });
    return response.data;
  }

  async resetPassword(
    token: string,
    email: string,
    password: string,
    passwordConfirmation: string
  ): Promise<{ message: string }> {
    const response = await api.post('/reset-password', {
      token,
      email,
      password,
      password_confirmation: passwordConfirmation,
    });
    return response.data;
  }

  async verifyResetToken(
    token: string,
    email: string
  ): Promise<{ valid: boolean; message: string }> {
    const response = await api.post('/verify-reset-token', { token, email });
    return response.data;
  }

  async changePassword(payload: {
    current_password?: string;
    password: string;
    password_confirmation: string;
  }): Promise<{ message: string }> {
    const response = await api.post('/change-password', payload);
    return response.data;
  }
}

export default new AuthService();
